import {Component, OnDestroy, OnInit, Renderer2, ViewChild} from '@angular/core';
import {EntityScopeEnum} from '@docma-mm-core/entity/entity-scope.enum';
import {Layout} from '@docma-mm-core/layout/layout';
import {AppContainerService} from '@mydocma/app-container';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {TreeNode, TreeOptions, TreeService} from '@mydocma/component-library/src/lib/tree';
import {BehaviorSubject, combineLatest, firstValueFrom, from, Subscription} from 'rxjs';
import {filter, map, switchMap, tap} from 'rxjs/operators';
import {MmProjectDownloadService} from '../../../mm-project/mm-download.service';
import {MmProjectService} from '../../../mm-project/mm-project.service';
import {MmViewService} from '../../../mm-view.service';
import {MmFilterService} from '../../mm-filter.service';
import {MmLayoutStateService} from '../layout-state.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ProjectActionPermissionEnum} from '@docma-mm-core/permission/project-action-permission.enum';
import {MmProjectPermissionsService} from '../../../mm-project/mm-project-permissions-service';
import {ActionPermissionEnum} from '@docma-mm-core/permission/action-permission.enum';
import {LayoutFavoriteFilterModeEnum} from '../layout-favorite-filter-mode.enum';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {LayoutEditService} from '../layout-edit/layout-edit-service';
import {Project as MmProject} from '@docma-mm-core/project/project';
import {MmProjectLoaderService} from '../../../mm-project/mm-project-loader.service';
import {User} from '@docma-mm-core/user/user';
import {AgGridAngular} from 'ag-grid-angular';
import {LayoutStateModel} from '../layout-state.model';
import {PersonalizationService} from '@docma-mm-core/personalization/personalization.service';
import {DeviceService} from '@mydocma/core/src/lib/device';
import {SettingNamesEnum} from '../../../mydocma-settings/setting-names.enum';
import {GetValue, SettingsService} from '../../../mydocma-settings/settings.service';
import {Setting} from '../../../mydocma-settings/setting';
import {ProjectService} from '@mydocma/project';
import {clearDictionary} from '@docma-mm-core/utils/utils';
import {MatLegacyButton} from '@angular/material/legacy-button';
import { PlatformModeService } from '../../../mm-offline/services/platform-mode.service';
import { TranslateService } from '@ngx-translate/core';
import { ConnectivityService } from '@mydocma/core';
import { PromptDialogService } from '@mydocma/component-library/src/lib/prompt-dialog';
import { Logger } from '@mydocma/common/src/lib/logger';
import { MmPlansManagerService } from '../../../mm-faults/mm-plans-view/services/mm-plans-manager.service';
import { MmFaultsSplitService } from '../../../mm-faults/mm-faults-view/services/mm-faults-split.service';

interface LayoutScope {
  scope: number;
  text?: string;
  icon?: string;
  layouts?: Layout[];
}


@UntilDestroy()
@Component({
  selector: 'mydocma-mm-layout-left-pane',
  templateUrl: './layout-left-pane.component.html',
  styleUrls: ['./layout-left-pane.component.scss']
})
export class MmLayoutLeftPaneComponent implements OnInit, OnDestroy {

  constructor(public readonly layoutStateService: MmLayoutStateService,
              private readonly mmViewService: MmViewService,
              public readonly appContainerService: AppContainerService,
              private readonly mmFilterService: MmFilterService,
              private readonly mmProjectService: MmProjectService,
              private readonly projectService: ProjectService,
              private readonly notificationService: NotificationService,
              private readonly downloadService: MmProjectDownloadService,
              public readonly permissionService: MmProjectPermissionsService,
              private readonly personalizationService: PersonalizationService,
              public readonly layoutEditService: LayoutEditService,
              public deviceService: DeviceService,
              private renderer2: Renderer2,
              private readonly projectLoaderService: MmProjectLoaderService,
              private readonly mmPersonalizationService: PersonalizationService,
              private readonly settingsService: SettingsService,
              private readonly platformModeService: PlatformModeService,
              private translate: TranslateService,
              private connectivityService: ConnectivityService,
              private readonly dialogService: PromptDialogService,
              private readonly logger: Logger,
              private readonly plansManagerService: MmPlansManagerService,
              private readonly faultsSplitService: MmFaultsSplitService) {

    this.personalizationService.getValue(PersonalizationKeysEnum.CurrentLayoutFavoriteFilterMode).then(value => this.layoutFavoriteFilterModeSubject.next(value ?? LayoutFavoriteFilterModeEnum.showFavorites));


    combineLatest([
      this.layoutStateService.loading,
      this.loadingProjectLayouts,
      this.mmProjectService.loadingState,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([l1, l2, l3]) => {
        setTimeout(() => {
          this.loading.next(l1 || l2 || l3);
        });
      });

    this.layoutFavoriteFilterModeSubject
      .pipe(untilDestroyed(this))
      .subscribe(async mode => {
       this.notifyFilter();
      });

    this.layoutTreeOptions = {
      suppressChildrenCount: true,
      toolbar: {
        buttons: {
          expandCollapseAll: {hidden: true},
        }
      },
      getNodeId: (data: any) => {
        if (data.scope) {
          return 'scope-' + data.scope.toString();
        } else {
          return data.Id.toString();
        }
      },
      getNodeInfo: (node: TreeNode) => {
        if (node.data.scope) {
          const isExpanded = this.getNodeExpandedState(node);
          return {
            text: node.data.text,
            icon: node.data.icon,
            expanded: isExpanded,
            selectable: !!node.data.Id
          };
        }
        return {
          text: node.data.Name
        };
      },
      useFilterCallbackForEmptyFilter: true,
      isNodeInFilter: (node: TreeNode, filters: string[]) => {
        const filterStr = ((this.treeService.filterService.filters?.length ?? 0) > 0 ? this.treeService.filterService.filters[0].toLowerCase() : null);

        if (node.data.scope) {
          const hasVisibleLayouts = (node.data.layouts.length > 0 && node.data.layouts.some(l => !(filterStr != null && l.Name.toLowerCase().indexOf(filterStr) === -1) || (this.layoutFavoriteFilterModeSubject.value === LayoutFavoriteFilterModeEnum.showFavorites && (!l.IsFavoriteAll && !l.IsFavoriteUser))));
          if (!hasVisibleLayouts) {
            return false;
          }
        }
        else {
          if ( (filterStr != null && node.data.Name.toLowerCase().indexOf(filterStr) === -1) || (this.layoutFavoriteFilterModeSubject.value === LayoutFavoriteFilterModeEnum.showFavorites && (!node.data.IsFavoriteAll && !node.data.IsFavoriteUser)) ) {
            return false;
          }
        }
        return null;
      },
      getNodeClass: (node: TreeNode) => {
        if (node.data.scope) {
          return 'layout-scope-node';
        } else if (!node.data.scope && (node.data.IsFavoriteAll || node.data.IsFavoriteUser) && this.layoutFavoriteFilterModeSubject.value == LayoutFavoriteFilterModeEnum.showAll) {
          return 'layout-node-with-icon';
        } else if (!node.data.scope && !node.data.IsFavoriteAll && !node.data.IsFavoriteUser) {
          return 'layout-node-with-icon';
        }
        return 'layout-node';
      },
      getNodeChildren: (node: TreeNode) => {
        if (node.data.scope) {
          return node.data.layouts;
        }
        return [];
      },
      onReady: async (ts: TreeService) => {
        this.treeService = ts;

        this.treeService.onDataLoaded.pipe(untilDestroyed(this)).subscribe(async nodes => {
          if (this.pendingLayoutSelection && nodes?.length > 0) {
            this.selectPendingLayout();
          } else if (this.currentSelectedLayoutId) {
            this.forceSelectLayout(this.currentSelectedLayoutId);
          }

          this.subscriptions.push(this.treeService.nodes$.pipe(untilDestroyed(this)).subscribe(async nodes => {
            const f = await firstValueFrom(this.treeService.filterService.filters$);
            if (f && (this.treeService.filterService.filters?.length ?? 0) === 1 && this.treeService.filterService.filters[0] == '') {
              await this.loadNodeExpandedState(nodes);
            }
          }));

          this.subscriptions.push(this.treeService.onNodeExpanded.pipe(untilDestroyed(this)).subscribe(async n => {
            const hasFilter = await firstValueFrom(this.treeService.filterService.filters$.pipe(map(f => (f?.length ?? 0) > 0 && f[0] != '')) );
            if (!hasFilter && n) {
              if (this.nodeExpandedStates.hasOwnProperty(n.id)) {
                this.nodeExpandedStates[n.id].isExpanded = true;
              }
              else {
                this.nodeExpandedStates[n.id] = {node: n, isExpanded: true};
              }
            }
          }));

          this.subscriptions.push(this.treeService.onNodeCollapsed.pipe(untilDestroyed(this)).subscribe(async n => {
            const hasFilter = await firstValueFrom(this.treeService.filterService.filters$.pipe(map(f => (f?.length ?? 0) > 0 && f[0] != '')));
            if (!hasFilter && n) {
              if (this.nodeExpandedStates.hasOwnProperty(n.id)) {
                this.nodeExpandedStates[n.id].isExpanded = false;
              }
              else {
                this.nodeExpandedStates[n.id] = {node: n, isExpanded: false};
              }
            }
          }));

          if (this.currentSelectedLayoutId) {
            setTimeout(() => {
              this.forceSelectLayout(this.currentSelectedLayoutId);
            }, 100);
          }
        });

        this.treeService.filterService.filters$.pipe(untilDestroyed(this)).subscribe(async filter => {
          if (!!this.isLayoutFilterEventSuspended) {
            return;
          }
        });
      }
    };

    this.projectLoaderService.projectUserPermissions
      .pipe(untilDestroyed(this), tap(userPermissions => {
        this.user = userPermissions.data.User;
      })).toPromise();

    combineLatest([this.mmViewService.currentProject$,
                          this.appContainerService.activeContentButton.pipe(filter(b => b && b.id === 'filter-layout'), tap(() => clearDictionary(this.nodeExpandedStates))),
                          this.reloadSubject])
      .pipe(map(([currentProject, navButton]) => {
        if (!currentProject) {
          this.mmViewService.closeFilter();
          return;
        }
        if (navButton) {
          this.currentProject = currentProject;
          this.loadingProjectLayouts.next(true);
          this.layoutScopesSubject.next([]);
          return this.currentProject;
        }}),
        untilDestroyed(this))
      .pipe(switchMap(project =>
        this.downloadService.downloadLayouts(project).pipe(
          switchMap(layouts => from(this.loadScopeSortSetting()).pipe(
            switchMap(sortSetting => from(this.layoutEditService.getLayoutStates(layouts)).pipe(
              map(layoutState => ({layouts, scopeSortSetting: sortSetting, LayoutStates: layoutState})
              ))
            ))
          ))
      ))
      .subscribe(param => {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.subscriptions = [];
        this.scopeSortSetting = param.scopeSortSetting;
        this.layoutsLoaded = param.layouts;
        this.layoutEditService.layoutStatesSubject.next(param.LayoutStates);

        if (this.layoutFavoriteFilterModeSubject.value != null) {
          this.getLayoutScopes();
          this.layoutScopesSubject.next(this.layoutScopesLoaded);
        }
        this.loadingProjectLayouts.next(false);

        if (this.pendingLayoutSelection && this.treeService) {
          this.selectPendingLayout();
        } else if (this.currentSelectedLayoutId) {
          this.forceSelectLayout(this.currentSelectedLayoutId);
        }
      },
      err => {
        this.logger.error(err, '', 'MmLayoutLeftPaneComponent');
        this.notificationService.error('An error while loading project layouts');
        this.loadingProjectLayouts.next(false);
      });

    this.layoutEditService.layoutAddedSubject.pipe(untilDestroyed(this)).subscribe(async addedLayout => {
      this.addedNode(addedLayout);
    });

    this.layoutEditService.layoutChangedSubject.pipe(untilDestroyed(this)).subscribe(async updatedLayout => {
      await this.updateNode(updatedLayout);
    });

    this.layoutEditService.layoutDeletedSubject.pipe(untilDestroyed(this)).subscribe(async deletedLayout => {
      this.deleteNode(deletedLayout);
    });

    this.layoutStateService.userStateChanged$
      .pipe(untilDestroyed(this))
      .subscribe(async l => {
        const state: LayoutStateModel = (await firstValueFrom(this.layoutStateService.currentLayoutState$.pipe(untilDestroyed(this)))).state;
        if (state?.layout) {
          await this.layoutEditService.updateLayoutState(state.layout);
        }
      });

    this.layoutStateService.columnOrderChanged$
      .pipe(untilDestroyed(this))
      .subscribe(async l => {
        const state: LayoutStateModel = (await firstValueFrom(this.layoutStateService.currentLayoutState$.pipe(untilDestroyed(this))))?.state;
        if (state?.layout) {
          await this.layoutEditService.updateLayoutState(state.layout);
        }
      });
  }
  @ViewChild('tree') tree: AgGridAngular;
  isEllipsis = false;

  public layoutTreeOptions: TreeOptions;
  public loading = new BehaviorSubject<boolean>(false);
  treeService: TreeService;
  permissionEditProjectLayout$ = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.EditProjectLayout]);
  permissionEditGlobalLayout$ = this.permissionService.hasActionPermissions([ActionPermissionEnum.EditGlobalLayout]);
  permissionEditLayoutVisibility$ = this.permissionService.hasActionPermissions([ActionPermissionEnum.EditLayoutVisibility]);
  entityScopes = EntityScopeEnum;
  layoutsLoaded: Layout[];
  layoutScopesLoaded: LayoutScope[] = [];

  scopeSortSetting: { [scope: string]: number };
  currentProject: MmProject;
  user: User;
  layoutScopesSubject = new BehaviorSubject<LayoutScope[]>([]);
  layoutScopes$ = this.layoutScopesSubject.asObservable();
  scopeSort: string;
  layoutTypeSortSetting: Setting;
  public isMatMenuOpened: boolean;
  layoutFavoriteFilterModeSubject = new BehaviorSubject<LayoutFavoriteFilterModeEnum>(null);
  layoutFavoriteFilterMode$ = this.layoutFavoriteFilterModeSubject.asObservable();
  layoutFavoriteFilterModes = LayoutFavoriteFilterModeEnum;
  private loadingProjectLayouts = new BehaviorSubject<boolean>(false);

  public isDesktop: boolean;

  subscriptions: Subscription[] = [];

  isLayoutFilterEventSuspended = false;

  private pendingLayoutSelection: {layoutId?: number, layoutSUid?: string} = null;

  public currentSelectedLayoutId: number = null;

  private isSelectionInProgress = false;

  private selectionTimerId: any = null;

  nodeExpandedStates: {[id: string]: {node: TreeNode, isExpanded: boolean}} = {};

  reloadSubject = new BehaviorSubject<any>(null);

  private langChangeSub?: Subscription;

  private isRevertingSelection = false;

  private forceSelectLayout(layoutId: number): void {
    if (!this.treeService || !layoutId) {
      return;
    }

    if (this.selectionTimerId) {
      clearTimeout(this.selectionTimerId);
      this.selectionTimerId = null;
    }

    if (this.isSelectionInProgress) {
      this.selectionTimerId = setTimeout(() => {
        this.forceSelectLayout(layoutId);
      }, 150);
      return;
    }

    this.isSelectionInProgress = true;

    try {
      const layoutToSelect = this.layoutsLoaded?.find(l => l.Id === layoutId);
      if (!layoutToSelect) {
        this.isSelectionInProgress = false;
        return;
      }

      const scopeNode = this.treeService.getNodeById('scope-' + layoutToSelect.Scope.toString());
      if (scopeNode && !scopeNode.expanded) {
        this.treeService.expandNode(scopeNode);
      }

      const layoutNode = this.treeService.getNodeById(layoutId.toString());
      if (layoutNode) {
        this.treeService.selectNode(layoutNode);
      }
    } finally {
      setTimeout(() => {
        this.isSelectionInProgress = false;
      }, 50);
    }
  }

  private checkLayoutNavigationSelection() {
    const layoutSUid = sessionStorage.getItem('selectedLayoutSUid');
    const layoutId = sessionStorage.getItem('selectedLayoutId');

    if (layoutSUid || layoutId) {
      this.pendingLayoutSelection = {};

      if (layoutSUid) {
        this.pendingLayoutSelection.layoutSUid = layoutSUid;
      }

      if (layoutId) {
        this.pendingLayoutSelection.layoutId = parseInt(layoutId, 10);
      }
    }
  }

  private selectPendingLayout() {
    if (!this.pendingLayoutSelection || !this.treeService || !this.layoutsLoaded?.length) {
      return;
    }

    let layoutToSelect: Layout = null;

    if (this.pendingLayoutSelection.layoutSUid) {
      layoutToSelect = this.layoutsLoaded.find(l => l.SUid === this.pendingLayoutSelection.layoutSUid);
    }

    if (!layoutToSelect && this.pendingLayoutSelection.layoutId) {
      layoutToSelect = this.layoutsLoaded.find(l => l.Id === this.pendingLayoutSelection.layoutId);
    }

    if (layoutToSelect) {
      this.currentSelectedLayoutId = layoutToSelect.Id;

      this.mmFilterService.setLayout(layoutToSelect);
      this.layoutStateService.setLayout(layoutToSelect);

      this.forceSelectLayout(layoutToSelect.Id);
    }

    this.pendingLayoutSelection = null;
  }

  private notifyFilter() {
    if (!this.treeService?.filterService){
      return;
    }

    if ((this.treeService.filterService.filters?.length ?? 0) === 0) {
      this.treeService.filterService.filters = [];
    }
    else if ((this.treeService.filterService.filters?.length ?? 0) === 1) {
      this.treeService.filterService.filters = [...this.treeService.filterService.filters];
    }
  }

  getNodeExpandedState(node: TreeNode) {
    if (this.nodeExpandedStates.hasOwnProperty(node.id)) {
      return this.nodeExpandedStates[node.id].isExpanded;
    }
    return true;
  }

  async loadNodeExpandedState(nodes: TreeNode[]) {
    Object.keys(this.nodeExpandedStates).forEach(key => {
      const n = nodes.find(f => f.id === key);
      if (n) {
        if (this.nodeExpandedStates[key].isExpanded) {
          this.treeService.expandNode(n);
        } else {
          this.treeService.collapseNode(n);
        }
      }
    });

    if (this.currentSelectedLayoutId) {
      setTimeout(() => {
        this.forceSelectLayout(this.currentSelectedLayoutId);
      }, 100);
    }
  }

  async loadScopeSortSetting(): Promise<{ [scope: string]: number }> {
    const defaultSortSetting: { [scope: string]: number } = {};
    defaultSortSetting[EntityScopeEnum.Project.toString()] = 1;
    defaultSortSetting[EntityScopeEnum.User.toString()] = 2;
    defaultSortSetting[EntityScopeEnum.Global.toString()] = 3;

    return await firstValueFrom(this.projectService.currentProject.pipe(
      switchMap(project => from(Promise.resolve(defaultSortSetting))
        .pipe(switchMap(defaultValue => from(this.settingsService.load(PersonalizationKeysEnum.LayoutTypeSorting, SettingNamesEnum.LayoutTypeSorting, project))
          .pipe(
            tap(value => {
              this.scopeSort = value;
            }),
            map(value => {
              return GetValue<{ [scope: string]: number }>(value, defaultValue);
            })
          )
        ))
      )));
  }

  async addedNode(param: { Layout: Layout, Layouts: Layout[] }) {
    this.layoutsLoaded = param.Layouts;

    const layoutScopes = this.getLayoutScopes();
    const layoutScopeNode = layoutScopes.find(x => x.scope === param.Layout.Scope);
    let nodeIndex = layoutScopeNode.layouts.findIndex(x => x.Id === param.Layout.Id);

    const newTreeNode = {
      data: param.Layout
    } as TreeNode;

    this.addNodeToSource(param.Layout, nodeIndex);
    nodeIndex = (nodeIndex === -1 ? 0 : nodeIndex);

    const addedNode = this.treeService.addNode(layoutScopeNode.layouts[nodeIndex], 'scope-' + param.Layout.Scope.toString(), nodeIndex);
    this.notifyFilter();

    this.treeService.gridApi.refreshCells({force: true});
    this.loadingProjectLayouts.next(false);
  }

  async updateNode(param: { Layout: Layout, Layouts: Layout[] }) {
    // Update the loaded layouts
    this.layoutsLoaded = param.Layouts;

    // Get the node to update from the tree
    let nodeToUpdate: { Node: Layout, Scope: LayoutScope } = this.getNode(param.Layout);

    if (!nodeToUpdate){
      const layoutScopeForNewLayout = this.layoutScopesLoaded.find(x => x.scope === param.Layout.Scope);
      nodeToUpdate = {Node: param.Layout, Scope: layoutScopeForNewLayout};
    }

    const favoriteChanged = (nodeToUpdate.Node.IsFavoriteUser || nodeToUpdate.Node.IsFavoriteAll) != (param.Layout.IsFavoriteUser || param.Layout.IsFavoriteAll);

    // Check if the scope has changed
    if (nodeToUpdate && nodeToUpdate.Node.Scope !== param.Layout.Scope || nodeToUpdate.Node.Name != param.Layout.Name) {
      // Update the node properties
      nodeToUpdate.Node.Name = param.Layout.Name;
      nodeToUpdate.Node.IsFavoriteUser = param.Layout.IsFavoriteUser;
      nodeToUpdate.Node.IsFavoriteAll = param.Layout.IsFavoriteAll;
      nodeToUpdate.Node.Scope = param.Layout.Scope;
      nodeToUpdate.Node.User = param.Layout.User;
      nodeToUpdate.Node.Project = param.Layout.Project;

      // Get layout scopes and find the scope node to move the updated node to
      const layoutScopes = this.getLayoutScopes();
      const targetScopeNode = layoutScopes.find(x => x.scope === param.Layout.Scope);
      const nodeIndex = targetScopeNode.layouts
        .findIndex(x => x.Id === param.Layout.Id);

      // Remove the node from the current scope and add it to the new scope
      this.treeService.removeNode(nodeToUpdate.Node);
      this.treeService.addNode(nodeToUpdate.Node, 'scope-' + param.Layout.Scope.toString(), nodeIndex);
    }
    else if (nodeToUpdate) {
      nodeToUpdate.Node.Name = param.Layout.Name;
      nodeToUpdate.Node.IsFavoriteUser = param.Layout.IsFavoriteUser;
      nodeToUpdate.Node.IsFavoriteAll = param.Layout.IsFavoriteAll;
      this.treeService.updateNode(nodeToUpdate.Node);
    }

    this.notifyFilter();
  }

  async deleteNode(param: { Layout: Layout, Layouts: Layout[] }) {
    const nodeIndex = this.layoutsLoaded.findIndex(x => x.Id === param.Layout.Id);
    this.treeService.removeNode(this.layoutsLoaded[nodeIndex]);
    this.removeNodeFromSource(param.Layout);

    this.layoutsLoaded = param.Layouts;
    this.loadingProjectLayouts.next(false);
    this.notifyFilter();
  }

  getNode(layout: Layout): {Node: Layout, Scope: LayoutScope} {
    let n: {Node: Layout, Scope: LayoutScope} = null;

    this.layoutScopesLoaded.forEach(sc => {
      const node = sc.layouts.find(x => x.Id === layout.Id);
      if (node) {
        n = {Node: node, Scope: sc};
        return;
      }
    });
    return n;
  }

  removeNodeFromSource(layout: Layout) {
    const n: {Node: Layout, Scope: LayoutScope} = null;

    this.layoutScopesLoaded.forEach(sc => {
      const i = sc.layouts.findIndex(x => x.Id === layout.Id);
      if (i !== -1) {
        sc.layouts.splice(i, 1);
      }
    });
  }

  addNodeToSource(layout: Layout, index: number): boolean {
    let isAdded = false;
    this.layoutScopesLoaded.forEach(sc => {
      if (sc.scope === layout.Scope) {
        if (sc.layouts.findIndex(x => x.Id === layout.Id) === -1) {
          console.warn(`LAYOUT ADDED TO NODE: ${layout.Name} (0)`);
          sc.layouts.splice(index, 0, layout);
          isAdded = true;
        }
      }
    });
    return isAdded;
  }

  getLayoutScopes() {
    const layoutScopes: LayoutScope[] = [];
    this.layoutScopesLoaded = [];

    const globalScope = this.createLayoutScope(EntityScopeEnum.Global);
    const projectScope = this.createLayoutScope(EntityScopeEnum.Project);
    const userScope = this.createLayoutScope(EntityScopeEnum.User);
    const localScope = this.createLayoutScope(EntityScopeEnum.Local);

    layoutScopes.push(localScope);
    layoutScopes.push(globalScope);
    layoutScopes.push(projectScope);
    layoutScopes.push(userScope);

    localScope.layouts.push(...this.layoutsLoaded
      .filter(x => x.Scope === EntityScopeEnum.Local)
      .sort((a, b) => (a.Name ?? '').localeCompare((b.Name ?? ''))));

    if (localScope.layouts[0]){
      localScope.layouts[0].Name = this.translate.instant('MM.layout.localLayoutName');
    }

    globalScope.layouts.push(...this.layoutsLoaded
      .filter(x => x.Scope === EntityScopeEnum.Global)
      .sort((a, b) => (a.Name ?? '').localeCompare((b.Name ?? ''))));

    projectScope.layouts.push(...this.layoutsLoaded
      .filter(x => x.Scope === EntityScopeEnum.Project)
      .sort((a, b) => (a.Name ?? '').localeCompare((b.Name ?? ''))));

    userScope.layouts.push(...this.layoutsLoaded
      .filter(x => x.Scope === EntityScopeEnum.User)
      .sort((a, b) => (a.Name ?? '').localeCompare((b.Name ?? ''))));

    this.layoutScopesLoaded = layoutScopes.sort((a, b) => this.scopeSortSetting[a.scope] - this.scopeSortSetting[b.scope]);
    return this.layoutScopesLoaded;
  }

  ngOnDestroy(): void {
    if (this.selectionTimerId) {
      clearTimeout(this.selectionTimerId);
      this.selectionTimerId = null;
    }

    this.isSelectionInProgress = false;
    this.currentSelectedLayoutId = null;
    this.pendingLayoutSelection = null;
  }

  ngOnInit() {
    this.isDesktop = this.deviceService.isDesktop();

    this.checkLayoutNavigationSelection();

    this.layoutStateService.currentLayoutState$
      .pipe(
        untilDestroyed(this),
        filter(layoutState => !!layoutState?.state?.layout)
      )
      .subscribe(layoutState => {
        if (layoutState?.state?.layout) {
          this.currentSelectedLayoutId = layoutState.state.layout.Id;

          if (this.treeService) {
            if (this.selectionTimerId) {
              clearTimeout(this.selectionTimerId);
            }

            this.selectionTimerId = setTimeout(() => {
              this.forceSelectLayout(layoutState.state.layout.Id);
            }, 50);
          }
        }
      });

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      const updatedScopes = this.layoutScopesSubject.value.map(scope => {
        if (scope.scope === EntityScopeEnum.Local && scope.layouts?.length) {
          return {
            ...scope,
            layouts: scope.layouts.map(layout => ({
              ...layout,
              Name: this.translate.instant('MM.layout.localLayoutName')
            }))
          };
        }
        return scope;
      });

      this.layoutScopesSubject.next(updatedScopes);
    });
  }

  onLayoutClick(layout: Layout) {
    if (this.isRevertingSelection) {
      this.isRevertingSelection = false;
      return;
    }
    const isNewSelection = this.currentSelectedLayoutId !== layout?.Id;
    if (this.connectivityService.isOfflineVal && !!layout && isNewSelection && layout?.Scope !== EntityScopeEnum.Local) {
      this.dialogService.showOfflineNotificationDialog();
      if (this.currentSelectedLayoutId && this.treeService) {
        this.isRevertingSelection = true;
        Promise.resolve().then(() => {
          this.treeService.selectNode(this.currentSelectedLayoutId.toString());
        });
      }
      return;
    }
    this.currentSelectedLayoutId = layout?.Id;

    if (layout?.Scope) {
      if (layout.Scope === EntityScopeEnum.Local) {
        this.mmFilterService.clearAllFilters();
        this.layoutStateService.setSearchFilter([]);
      }
      this.platformModeService.setLocalMode(layout.Scope === EntityScopeEnum.Local);
      const shouldReloadSelectedPlan = isNewSelection
        && !!this.plansManagerService.selectedPlan
        && this.faultsSplitService.rightViewVisible;

      if (shouldReloadSelectedPlan) {
        this.plansManagerService.setSelectedPlan(this.plansManagerService.selectedPlan, true);
      }
    }

    this.mmFilterService.setLayout(layout);
    this.layoutStateService.setLayout(layout);
  }

  onCloseClick() {
    this.mmViewService.closeFilter();
  }

  async switchLayoutsFilter() {
    if (this.layoutFavoriteFilterModeSubject.value == LayoutFavoriteFilterModeEnum.showAll) {
      this.layoutFavoriteFilterModeSubject.next(LayoutFavoriteFilterModeEnum.showFavorites);
    } else {
      this.layoutFavoriteFilterModeSubject.next(LayoutFavoriteFilterModeEnum.showAll);
    }

    this.personalizationService.setValue(PersonalizationKeysEnum.CurrentLayoutFavoriteFilterMode, this.layoutFavoriteFilterModeSubject.value);
  }

  async toTopPosition(node: TreeNode) {
    if (!this.isAppOnline(true)){
      return;
    }
    const layout = Object.assign(new Layout(), node.data);
    const isExpanded = node.expanded;

    const sortedOthers = Object.keys(this.scopeSortSetting)
      .filter(key => +key !== node.data.scope)
      .sort((a, b) => +this.scopeSortSetting[a] - +this.scopeSortSetting[b]);

    for (let i = 0; i < sortedOthers.length; i++) {
      this.scopeSortSetting[sortedOthers[i]] = (i + 2);
    }
    this.scopeSortSetting[node.data.scope] = 1;

    await firstValueFrom(this.projectService.currentProject.pipe(
      switchMap(project => this.settingsService.save(JSON.stringify(this.scopeSortSetting), PersonalizationKeysEnum.LayoutTypeSorting, SettingNamesEnum.LayoutTypeSorting, project))
    ));

    // if (!this.scopeSortSetting) {
    //   this.userSettingApiService.createUserSetting({VariableName: 'LayoutTypeSorting', SettingValue: JSON.stringify(this.sortSetting)}).toPromise();
    // }
    // else {
    //   this.userSettingApiService.putUserSetting({VariableName: 'LayoutTypeSorting', SettingValue: JSON.stringify(this.sortSetting)}).toPromise();
    // }

    this.reloadSubject.next(true);

  }

  addLayout(node: any) {
    if (!this.isAppOnline(true)){
      return;
    }
    this.layoutEditService.addLayout(node.data.scope, this.currentProject, this.user);
  }

  editLayout(node: any) {
    const layout = Object.assign(new Layout(), node.data);
    this.layoutEditService.editLayout(layout.Id, layout.Scope, this.currentProject, this.user);
  }

  async duplicateLayout(node: any) {
    const layout = Object.assign(new Layout(), node.data);
    await this.layoutEditService.duplicateLayout(layout, this.layoutsLoaded, this.currentProject, this.user);
  }

  async deleteLayout(node: any) {
    const layout = Object.assign(new Layout(), node.data);
    await this.layoutEditService.deleteLayout(layout, this.currentProject);
  }

  saveForAll(node: any) {
    const layout = Object.assign(new Layout(), node.data);
    this.layoutEditService.saveForAll(layout, this.currentProject);
  }

  async revert(node: any) {
    const layout = Object.assign(new Layout(), node.data);
    this.layoutEditService.revert(layout, this.currentProject, true);
  }

  public toggleMenu(value: MatLegacyButton, state: 'close' | 'open'): void {

    const elem = value._elementRef.nativeElement.closest('.mydocma-node');
    if (state === 'close') {
      this.renderer2.removeClass(elem, 'item-menu-open');
    } else {
      this.renderer2.addClass(elem, 'item-menu-open');
    }

  }

  private createLayoutScope(scope: EntityScopeEnum): LayoutScope {
    const layoutScope: LayoutScope = {scope, layouts: []};
    layoutScope.layouts = [];
    switch (scope) {
      case EntityScopeEnum.User:
        layoutScope.text = 'MM.layout.scopeUserText';
        layoutScope.icon = 'person_outline';
        break;
      case EntityScopeEnum.Project:
        layoutScope.text = 'MM.layout.scopeProjectText';
        layoutScope.icon = 'domain';
        break;
      case EntityScopeEnum.Global:
        layoutScope.text = 'MM.layout.scopeGlobalText';
        layoutScope.icon = 'language';
        break;
      case EntityScopeEnum.Local:
        layoutScope.text = 'MM.layout.scopeLocalText';
        layoutScope.icon = 'home';
        break;

      default:
        break;
    }
    return layoutScope;
  }

  // it should be function so change detection would trigger to bind new value for tooltip
  public getTooltipText(node: any): string {
    return this.isEllipsis ? node.text : '';
  }

  public onMouseEnter($event: HTMLSpanElement) {
    this.isEllipsis = $event.offsetWidth < $event.scrollWidth;
  }

  isAppOnline(showNotification: boolean) {
    if (showNotification && this.connectivityService.isOfflineVal) {
      this.dialogService.showOfflineNotificationDialog();
    }
    return !this.connectivityService.isOfflineVal;
  }
}
